import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { TFunction } from 'i18next';
import { z } from 'zod';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  Plus,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { bookingService } from '@/services/api/booking.service';
import { adminBookingService } from '@/services/api/admin-booking.service';
import { organizationService } from '@/services/api/organization.service';
import { customerService } from '@/services/api/customer.service';
import type { Service } from '@/types/booking';
import type { Customer } from '@/types/customer';

import { Button } from '@/components/ui/button';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TimePicker } from '@/components/ui/time-picker';
import { Badge } from '@/components/ui/badge';
import { SearchBar } from '@/components/ui/search-bar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Stepper, StepCard, type StepState } from '@/components/booking/Stepper';
import { BookingSummaryCard } from '@/components/booking/BookingSummaryCard';
import { BookingTypeStep, type BookingType } from '@/components/booking/BookingTypeStep';
import { GroupOccurrencesStep, isSelectable } from '@/components/booking/GroupOccurrencesStep';
import { CustomerFormDialog } from '@/components/customers/CustomerFormDialog';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';

// Only booking-level fields here — customers are chosen from existing records.
const detailsSchema = z.object({
  notes: z.string().optional(),
  status: z.enum(['pending', 'confirmed']),
});

type DetailsFormData = z.infer<typeof detailsSchema>;
type WizardStep = 0 | 1 | 2 | 3;

const SEARCH_DEBOUNCE_MS = 250;

function getQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

function isGroupService(svc: Service): boolean {
  const cap = svc.max_capacity ?? svc.maxCapacity ?? 1;
  return cap > 1 || !!(svc.group_booking ?? svc.groupBooking);
}

function isOneOnOneService(svc: Service): boolean {
  const cap = svc.max_capacity ?? svc.maxCapacity ?? 1;
  return cap <= 1;
}

// Mon-first ordering to match the weekday picker used elsewhere (recurrence step).
const WEEKDAYS: Array<{ value: number; shortKey: string }> = [
  { value: 1, shortKey: 'mon' },
  { value: 2, shortKey: 'tue' },
  { value: 3, shortKey: 'wed' },
  { value: 4, shortKey: 'thu' },
  { value: 5, shortKey: 'fri' },
  { value: 6, shortKey: 'sat' },
  { value: 0, shortKey: 'sun' },
];

// Time-of-day presets prefill the From/To window. Selecting "Morning" or
// "Afternoon" sets these times; typing a different range clears the highlight.
const TIME_PERIODS = [
  { key: 'morning', from: '06:00', to: '12:00' },
  { key: 'afternoon', from: '12:00', to: '18:00' },
] as const;

export default function NewGroupRecurringBookingPage() {
  const [, setLocation] = useLocation();
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation('bookings');
  const queryClient = useQueryClient();

  useSetPageHeader(
    {
      title: t('groupRecurring.title', 'Recurring group booking'),
      sub: t('groupRecurring.subtitle', 'Enrol a customer into several sessions at once'),
    },
    [t],
  );

  const { data: organization } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationService.getOrganization(),
  });
  void organization;

  const presetServiceId = useMemo(() => getQueryParam('service_id'), []);
  const presetCustomerId = useMemo(() => getQueryParam('customer_id'), []);

  // Arriving with a preset service (from the recurring wizard) means the
  // booking-type + service choices are already made — land on the service step
  // so Booking Type shows as a completed step 1.
  const [currentStep, setCurrentStep] = useState<WizardStep>(presetServiceId ? 1 : 0);
  const [bookingType, setBookingType] = useState<BookingType>('recurring');
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  // Customers — multi-select of existing records.
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('');
  const [isNewCustomerOpen, setIsNewCustomerOpen] = useState(false);

  // Occurrences + filters
  const [selectedSlots, setSelectedSlots] = useState<Set<number>>(new Set());
  const [weekdayFilter, setWeekdayFilter] = useState<Set<number>>(new Set());
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const activePeriod = TIME_PERIODS.find((p) => p.from === fromTime && p.to === toTime)?.key ?? null;
  const hasTimeFilter = fromTime !== '' || toTime !== '';

  const { control, watch } = useForm<DetailsFormData>({
    resolver: zodResolver(detailsSchema),
    defaultValues: { notes: '', status: 'confirmed' },
  });
  const formData = watch();

  const requiredSeats = Math.max(1, selectedCustomers.length);

  function toggleCustomer(customer: Customer) {
    setSelectedCustomers((prev) =>
      prev.some((c) => c.id === customer.id)
        ? prev.filter((c) => c.id !== customer.id)
        : [...prev, customer],
    );
  }

  const { data: services = [], isLoading: isLoadingServices } = useQuery({
    queryKey: ['services'],
    queryFn: () => bookingService.getServices(),
  });
  // Show every recurring-capable service here (not just group ones) so the
  // service step works as a full picker. Picking a 1:1 service hands back to
  // the standard recurring wizard.
  const recurringServices = useMemo(
    () => services.filter((s) => s.active && (isGroupService(s) || isOneOnOneService(s))),
    [services],
  );
  const groupServices = useMemo(
    () => recurringServices.filter(isGroupService),
    [recurringServices],
  );

  function handleServiceSelect(svc: Service) {
    if (isGroupService(svc)) {
      setSelectedService(svc);
      setSelectedSlots(new Set());
      return;
    }
    // 1:1 service → continue in the standard recurring wizard.
    const params = new URLSearchParams({ step: '2' });
    if (presetCustomerId) params.set('customer_id', presetCustomerId);
    setLocation(`/admin/bookings/new/recurring?${params.toString()}`);
  }

  // Preselect service from query param.
  useEffect(() => {
    if (selectedService || !presetServiceId || groupServices.length === 0) return;
    const match = groupServices.find((s) => String(s.id) === presetServiceId);
    if (match) setSelectedService(match);
  }, [presetServiceId, groupServices, selectedService]);

  // Preload customer when ?customer_id=… is present.
  const { data: presetCustomer } = useQuery({
    queryKey: ['customer', presetCustomerId],
    queryFn: () => customerService.getCustomer(presetCustomerId!),
    enabled: !!presetCustomerId,
  });
  useEffect(() => {
    if (presetCustomer) {
      setSelectedCustomers((prev) =>
        prev.some((c) => c.id === presetCustomer.id) ? prev : [...prev, presetCustomer],
      );
    }
  }, [presetCustomer]);

  // Customer search debounce
  useEffect(() => {
    const id = setTimeout(() => setDebouncedCustomerSearch(customerSearch.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [customerSearch]);

  const { data: customerSearchResult, isFetching: isSearchingCustomers } = useQuery({
    queryKey: ['customer-search', debouncedCustomerSearch],
    queryFn: () => customerService.list({ search: debouncedCustomerSearch || undefined, page: 1, perPage: 8 }),
    enabled: currentStep === 2,
  });

  const weekdaysParam = useMemo(() => Array.from(weekdayFilter).sort(), [weekdayFilter]);
  const customerIdsParam = useMemo(
    () => selectedCustomers.map((c) => c.publicId ?? c.id),
    [selectedCustomers],
  );

  const {
    data: occurrencesData,
    isFetching: isLoadingOccurrences,
  } = useQuery({
    queryKey: ['group-occurrences', selectedService?.id, weekdaysParam, fromTime, toTime, customerIdsParam],
    queryFn: () =>
      adminBookingService.getGroupOccurrences({
        service_id: selectedService!.id,
        customer_ids: customerIdsParam.length ? customerIdsParam : undefined,
        weekdays: weekdaysParam.length ? weekdaysParam : undefined,
        from_time: fromTime || undefined,
        to_time: toTime || undefined,
      }),
    enabled: !!selectedService && currentStep === 3,
  });
  const occurrences = occurrencesData?.occurrences ?? [];
  const occurrencesCapped = occurrencesData?.capped ?? false;

  // Drop selections that became unselectable after the customer set changed
  // (e.g. adding a customer pushes a 2-spot session over capacity). Only prunes
  // slots present in the current view, so selections hidden by an active filter
  // are preserved.
  useEffect(() => {
    setSelectedSlots((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set(prev);
      occurrences.forEach((o) => {
        if (next.has(o.slot_id) && !isSelectable(o, requiredSeats)) {
          next.delete(o.slot_id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [occurrences, requiredSeats]);

  function toggleWeekday(value: number) {
    setWeekdayFilter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  const createMutation = useMutation({
    mutationFn: async (data: DetailsFormData) => {
      if (!selectedService) throw new Error(t('groupRecurring.errors.serviceRequired', 'Select a service'));
      return adminBookingService.createGroupRecurringBooking({
        service_id: selectedService.id,
        slot_ids: Array.from(selectedSlots),
        customer_ids: selectedCustomers.map((c) => c.publicId ?? c.id),
        notes: data.notes || undefined,
        initial_status: data.status,
      });
    },
    onSuccess: (result) => {
      if (result.count > 0) {
        toast.success(
          t('groupRecurring.toasts.created', '{{count}} bookings created', { count: result.count }),
        );
      }
      // Tell the admin which sessions didn't take. `already_enrolled` is
      // informational (the customer was there already); full / capacity
      // skips mean a chosen session was dropped.
      const droppedSlots = new Set(
        (result.skipped ?? [])
          .filter((s) => s.reason !== 'already_enrolled')
          .map((s) => s.slot_id),
      );
      if (droppedSlots.size > 0) {
        toast.warning(
          t('groupRecurring.toasts.skipped', '{{count}} sessions skipped (full or not enough seats)', {
            count: droppedSlots.size,
          }),
        );
      }
      const alreadyEnrolled = (result.skipped ?? []).filter((s) => s.reason === 'already_enrolled').length;
      if (alreadyEnrolled > 0) {
        toast.info(
          t('groupRecurring.toasts.alreadyEnrolled', '{{count}} enrolments already existed and were left as is', {
            count: alreadyEnrolled,
          }),
        );
      }
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
      setLocation('/admin/bookings');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.message || t('groupRecurring.errors.createFailed', 'Could not create bookings');
      toast.error(msg);
    },
  });

  // Step helpers
  const stepStateAt = (idx: number): StepState =>
    idx < currentStep ? 'done' : idx === currentStep ? 'active' : 'locked';
  const onEditAt = (idx: number) => (idx < currentStep ? () => setCurrentStep(idx as WizardStep) : undefined);

  const canService = selectedService !== null;
  const canCustomer = selectedCustomers.length > 0;

  // Switching to one-time hands back to the standard new-booking wizard.
  function handleBookingTypeNext() {
    if (bookingType === 'one_time') {
      const params = new URLSearchParams();
      if (presetCustomerId) params.set('customer_id', presetCustomerId);
      const qs = params.toString();
      setLocation(`/admin/bookings/new${qs ? `?${qs}` : ''}`);
      return;
    }
    setCurrentStep(1);
  }

  const bookingTypeSummary =
    bookingType === 'one_time'
      ? t('newBooking.bookingType.oneTime', 'One-time')
      : t('newBooking.bookingType.recurring', 'Recurring booking');

  function toggleSlot(slotId: number) {
    setSelectedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(slotId)) next.delete(slotId);
      else next.add(slotId);
      return next;
    });
  }

  function toggleAllSlots() {
    const selectable = occurrences
      .filter((o) => !o.full && !o.customer_already_booked && o.spots_remaining >= requiredSeats)
      .map((o) => o.slot_id);
    setSelectedSlots((prev) => {
      const allOn = selectable.every((id) => prev.has(id)) && selectable.length > 0;
      return allOn ? new Set() : new Set(selectable);
    });
  }

  // Summary
  const serviceSummary = selectedService
    ? `${selectedService.name} · ${selectedService.duration || selectedService.duration_minutes} min`
    : undefined;
  const customerSummary = (() => {
    if (selectedCustomers.length === 0) return undefined;
    if (selectedCustomers.length === 1) {
      return `${selectedCustomers[0].fullName} · ${selectedCustomers[0].email}`;
    }
    return t('groupRecurring.summary.customerCount', '{{count}} customers', { count: selectedCustomers.length });
  })();
  const dashSoft = <span className="text-soft">—</span>;
  const totalValue =
    selectedService && !selectedService.hide_price && selectedSlots.size && selectedCustomers.length
      ? formatCurrency((selectedService.price || 0) * selectedSlots.size * selectedCustomers.length)
      : undefined;
  const summaryRows = [
    { label: t('groupRecurring.summary.service', 'Service'), value: serviceSummary ?? dashSoft },
    { label: t('groupRecurring.summary.customer', 'Customers'), value: customerSummary ?? dashSoft },
    {
      label: t('groupRecurring.summary.occurrences', 'Sessions'),
      value: selectedSlots.size || dashSoft,
    },
  ];

  return (
    <div className="space-y-6" data-testid="group-recurring-wizard">
      <div className="mx-auto grid w-full max-w-6xl gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0">
          <Stepper active={currentStep + 1}>
            {/* Step 1 — Booking Type */}
            <StepCard
              step={1}
              title={t('newBooking.stepTitles.step1', 'Booking Type')}
              state={stepStateAt(0)}
              summary={bookingTypeSummary}
              onEdit={onEditAt(0)}
            >
              <BookingTypeStep value={bookingType} onChange={setBookingType} t={t} />
              <StepNav onPrev={undefined} onNext={handleBookingTypeNext} disabledNext={false} t={t} />
            </StepCard>

            {/* Step 2 — Service */}
            <StepCard
              step={2}
              title={t('groupRecurring.steps.service', 'Group service')}
              state={stepStateAt(1)}
              summary={serviceSummary}
              onEdit={onEditAt(1)}
            >
              {isLoadingServices ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
                </div>
              ) : recurringServices.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    {t('groupRecurring.noServices', 'No services available.')}
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="grid gap-2" role="radiogroup">
                  {recurringServices.map((s) => {
                    const selected = selectedService?.id === s.id;
                    const duration = s.duration || s.duration_minutes;
                    const group = isGroupService(s);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => handleServiceSelect(s)}
                        aria-checked={selected}
                        role="radio"
                        className={cn(
                          'text-left rounded-2xl border p-4 transition-colors',
                          selected ? 'border-brand-500 bg-brand-50 shadow-sm' : 'border-line hover:border-brand-300 bg-surface',
                        )}
                        data-testid="group-service-card"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="flex items-center gap-2 font-semibold text-ink">
                              {s.name}
                              {group && (
                                <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100 border border-violet-200">
                                  <Users className="h-3 w-3 mr-1" />
                                  {t('recurring.groupBadge', 'Group')}
                                </Badge>
                              )}
                            </p>
                            {s.description && (
                              <p className="text-body-sm text-soft mt-0.5 line-clamp-2">{s.description}</p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-body-sm text-soft">{duration} min</p>
                            {!s.hide_price && (
                              <p className="text-body-sm font-semibold text-ink">{formatCurrency(s.price || 0)}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              <StepNav onPrev={() => setCurrentStep(0)} onNext={() => setCurrentStep(2)} disabledNext={!canService} t={t} />
            </StepCard>

            {/* Step 3 — Customers */}
            <StepCard
              step={3}
              title={t('groupRecurring.steps.customer', 'Customers')}
              state={stepStateAt(2)}
              summary={customerSummary}
              onEdit={onEditAt(2)}
            >
              {/* Selected customers as removable chips */}
              {selectedCustomers.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2" data-testid="selected-customers">
                  {selectedCustomers.map((c) => (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1.5 rounded-pill border border-brand-200 bg-brand-50 py-1 pl-3 pr-1.5 text-body-sm"
                    >
                      <span className="font-medium text-ink">{c.fullName}</span>
                      <button
                        type="button"
                        onClick={() => toggleCustomer(c)}
                        aria-label={t('groupRecurring.removeCustomer', 'Remove')}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-soft hover:bg-brand-100 hover:text-ink"
                        data-testid="remove-customer"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex-1">
                  <SearchBar
                    placeholder={t('newBooking.customer.searchPlaceholder', 'Search by name or email…')}
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    data-testid="customer-search-input"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  iconLeft={<Plus className="h-4 w-4" />}
                  onClick={() => setIsNewCustomerOpen(true)}
                  data-testid="add-new-customer-btn"
                >
                  {t('groupRecurring.customer.addNew', 'Add new customer')}
                </Button>
              </div>
              {isSearchingCustomers && (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-soft" />
                </div>
              )}
              {!isSearchingCustomers && (customerSearchResult?.customers?.length ?? 0) > 0 && (
                <div className="mt-3 rounded-xl border border-line divide-y divide-line">
                  {customerSearchResult!.customers.map((c) => {
                    const picked = selectedCustomers.some((sc) => sc.id === c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggleCustomer(c)}
                        aria-pressed={picked}
                        className={cn(
                          'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
                          picked ? 'bg-brand-50' : 'hover:bg-surface-2',
                        )}
                        data-testid="customer-result"
                      >
                        <div className="h-9 w-9 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center font-semibold">
                          {c.fullName.slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-ink truncate">{c.fullName}</p>
                          <p className="text-body-sm text-soft truncate">
                            {c.email}
                            {c.phone ? ` · ${c.phone}` : ''}
                          </p>
                        </div>
                        {picked && <Check className="h-4 w-4 text-brand-600 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="mt-4 space-y-4">
                <FormField label={t('newBooking.form.initialStatus', 'Initial status')} icon={<FileText className="h-4 w-4" />}>
                  <Controller name="status" control={control} render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="confirmed">{t('newBooking.form.statusConfirmed', 'Confirmed')}</SelectItem>
                        <SelectItem value="pending">{t('newBooking.form.statusPending', 'Pending')}</SelectItem>
                      </SelectContent>
                    </Select>
                  )} />
                </FormField>
                <FormField label={t('newBooking.form.notesOptional', 'Notes (optional)')} icon={<FileText className="h-4 w-4" />}>
                  <Controller name="notes" control={control} render={({ field }) => (
                    <Textarea {...field} rows={3} placeholder={t('newBooking.form.notesPlaceholder', 'Add notes…')} />
                  )} />
                </FormField>
              </div>

              <StepNav
                onPrev={() => setCurrentStep(1)}
                onNext={() => setCurrentStep(3)}
                disabledNext={!canCustomer}
                t={t}
              />
            </StepCard>

            {/* Step 4 — Occurrences */}
            <StepCard step={4} title={t('groupRecurring.steps.occurrences', 'Select sessions')} state={stepStateAt(3)}>
              {/* Filters */}
              <div className="mb-5 space-y-3">
                <div>
                  <Label className="mb-2 block text-body-sm">{t('groupRecurring.filters.weekday', 'Day of week')}</Label>
                  <div className="grid grid-cols-7 gap-1.5" role="group" aria-label={t('groupRecurring.filters.weekday', 'Day of week')}>
                    {WEEKDAYS.map((d) => {
                      const active = weekdayFilter.has(d.value);
                      return (
                        <button
                          key={d.value}
                          type="button"
                          onClick={() => toggleWeekday(d.value)}
                          aria-pressed={active}
                          className={cn(
                            'h-10 rounded-lg border text-body-sm font-semibold uppercase tracking-wide transition-colors',
                            active
                              ? 'bg-brand-500 border-brand-500 text-white shadow-sm'
                              : 'bg-surface border-line text-ink hover:border-brand-300 hover:bg-brand-50',
                          )}
                          data-testid={`group-weekday-${d.shortKey}`}
                        >
                          {t(`timeSlots:days.${d.shortKey}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block text-body-sm">{t('groupRecurring.filters.timeOfDay', 'Time of day')}</Label>
                  <div className="flex flex-wrap items-center justify-between gap-2" role="group" aria-label={t('groupRecurring.filters.timeOfDay', 'Time of day')}>
                    <div className="flex items-center gap-2">
                      {TIME_PERIODS.map((p) => {
                        const active = activePeriod === p.key;
                        return (
                          <button
                            key={p.key}
                            type="button"
                            onClick={() => {
                              if (active) {
                                setFromTime('');
                                setToTime('');
                              } else {
                                setFromTime(p.from);
                                setToTime(p.to);
                              }
                            }}
                            aria-pressed={active}
                            className={cn(
                              'rounded-lg border px-4 h-10 text-body-sm font-medium transition-colors',
                              active
                                ? 'bg-brand-500 border-brand-500 text-white shadow-sm'
                                : 'bg-surface border-line text-ink hover:border-brand-300 hover:bg-brand-50',
                            )}
                            data-testid={`group-period-${p.key}`}
                          >
                            {p.key === 'morning'
                              ? t('groupRecurring.filters.morning', 'Morning')
                              : t('groupRecurring.filters.afternoon', 'Afternoon')}
                          </button>
                        );
                      })}
                    </div>

                    <span className="px-2 text-body-xs font-medium uppercase tracking-wide text-soft">
                      {t('groupRecurring.filters.or', '— or —')}
                    </span>

                    <div className="flex items-center gap-2">
                      <TimePicker
                        value={fromTime}
                        onChange={setFromTime}
                        placeholder={t('groupRecurring.filters.from', 'From')}
                        className="w-32"
                      />
                      <TimePicker
                        value={toTime}
                        onChange={setToTime}
                        placeholder={t('groupRecurring.filters.to', 'To')}
                        className="w-32"
                      />
                      {(weekdayFilter.size > 0 || hasTimeFilter) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setWeekdayFilter(new Set());
                            setFromTime('');
                            setToTime('');
                          }}
                          data-testid="group-clear-filters"
                        >
                          {t('groupRecurring.filters.clear', 'Clear')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {!isLoadingOccurrences && weekdayFilter.size === 0 && !hasTimeFilter && (
                  <p className="text-body-xs text-soft">
                    {t('groupRecurring.filters.defaultWindowHint', 'Showing the next month. Filter by day or time to see further ahead.')}
                  </p>
                )}
                {occurrencesCapped && (
                  <p className="text-body-xs text-soft">
                    {t('groupRecurring.filters.cappedHint', 'Showing the first {{max}} matches — narrow the filters to see fewer.', {
                      max: occurrencesData?.max ?? 60,
                    })}
                  </p>
                )}
              </div>

              <GroupOccurrencesStep
                occurrences={occurrences}
                selected={selectedSlots}
                onToggle={toggleSlot}
                onToggleAll={toggleAllSlots}
                isLoading={isLoadingOccurrences}
                requiredSeats={requiredSeats}
                t={t}
              />

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(2)} data-testid="back-button">
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  {t('newBooking.buttons.back', 'Back')}
                </Button>
                <Button
                  onClick={() => createMutation.mutate(formData)}
                  disabled={createMutation.isPending || selectedSlots.size === 0}
                  size="lg"
                  data-testid="create-group-series-button"
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      {t('groupRecurring.actions.creating', 'Creating…')}
                    </>
                  ) : (
                    <>
                      <Check className="h-5 w-5 mr-2" />
                      {t('groupRecurring.actions.create', 'Book {{count}} sessions', { count: selectedSlots.size })}
                    </>
                  )}
                </Button>
              </div>
            </StepCard>
          </Stepper>
        </div>

        <aside className="hidden lg:block">
          <BookingSummaryCard
            sticky
            title={t('groupRecurring.summary.title', 'Group series')}
            rows={summaryRows}
            totalLabel={t('groupRecurring.summary.total', 'Estimated total')}
            totalValue={totalValue}
            reassurance={null}
          />
        </aside>
      </div>

      <CustomerFormDialog
        open={isNewCustomerOpen}
        onOpenChange={setIsNewCustomerOpen}
        onSaved={(customer) => {
          setSelectedCustomers((prev) =>
            prev.some((c) => c.id === customer.id) ? prev : [...prev, customer],
          );
        }}
      />
    </div>
  );
}

function StepNav({
  onPrev,
  onNext,
  disabledNext,
  t,
}: {
  onPrev?: () => void;
  onNext: () => void;
  disabledNext: boolean;
  t: TFunction;
}) {
  return (
    <div className="mt-6 flex items-center justify-between gap-3">
      <Button type="button" variant="outline" onClick={onPrev} disabled={!onPrev} data-testid="back-button">
        <ChevronLeft className="h-4 w-4 mr-2" />
        {t('newBooking.buttons.back', 'Back')}
      </Button>
      <Button type="button" onClick={onNext} disabled={disabledNext} data-testid="next-button">
        {t('newBooking.buttons.next', 'Next')}
        <ChevronRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  );
}

function FormField({
  label,
  required,
  icon,
  error,
  children,
}: {
  label: React.ReactNode;
  required?: boolean;
  icon?: React.ReactNode;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        {icon}
        {label}
        {required && <span className="text-red-500">*</span>}
      </Label>
      {children}
      {error && <p className="text-body-sm text-red-500">{error}</p>}
    </div>
  );
}
